import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "kirchero_admin_token";

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ProductPickerModal({ open, onClose, onPick }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/products?limit=50`);
        const data = await res.json();
        if (!cancelled) setProducts(data.products || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // eslint-disable-next-line
    run();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="product-picker-modal"
    >
      <div
        className="bg-[#0a0a0a] border border-white/10 max-w-4xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
          <h3 className="font-display text-lg uppercase tracking-widest text-[#D4AF37]">
            Shopify-Produkt wählen
          </h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl"
            data-testid="product-picker-close"
          >
            ×
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center text-white/40">Lade Produkte …</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-6">
            {products.map((p) => (
              <button
                key={p.id || p.handle}
                type="button"
                onClick={() => onPick(p)}
                className="group text-left border border-white/10 hover:border-[#D4AF37] transition-colors bg-black/40 overflow-hidden"
                data-testid="product-picker-item"
              >
                <div className="aspect-square overflow-hidden bg-black">
                  <img
                    src={p.featuredImage?.url || p.images?.[0]?.url || ""}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <div className="p-3">
                  <div className="text-[11px] font-display uppercase tracking-widest text-white line-clamp-2">
                    {p.title}
                  </div>
                  <div className="text-xs text-[#D4AF37] mt-1">
                    {p.currency || "EUR"} {p.price}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LoginCard({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        toast.error("Falsches Passwort");
        return;
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Willkommen zurück, Hero. 👑");
      onSuccess();
    } catch (err) {
      toast.error("Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-md mx-auto mt-32 border border-white/10 bg-black/60 p-10"
      data-testid="admin-login-form"
    >
      <h1 className="font-display text-3xl uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
        Admin Bereich
      </h1>
      <p className="text-sm text-white/50 mb-8 font-body">
        Nur für autorisierte KirchHero-Crew.
      </p>
      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
        Passwort
      </label>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
        className="w-full bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
        data-testid="admin-password-input"
      />
      <button
        type="submit"
        disabled={loading || !password}
        className="mt-6 w-full bg-[#D4AF37] hover:bg-[#c89e2e] text-black font-display uppercase tracking-widest py-3 disabled:opacity-50 transition-colors"
        data-testid="admin-login-submit"
      >
        {loading ? "Lade …" : "Einloggen"}
      </button>
    </form>
  );
}

function Composer({ onPosted }) {
  const [caption, setCaption] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [aiFilename, setAiFilename] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [templateFilename, setTemplateFilename] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const fileRef = useRef(null);
  const tplRef = useRef(null);

  const onProductPick = async (p) => {
    setProductPickerOpen(false);
    const imageUrl = p.featuredImage?.url || p.images?.[0]?.url;
    if (!imageUrl) {
      toast.error("Produkt hat kein Bild");
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/import-product-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ image_url: imageUrl }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Produkt-Import fehlgeschlagen");
        return;
      }
      setAiFilename(data.filename);
      setImageFile(null);
      setPreview(`${API}${data.preview_path}`);
      if (fileRef.current) fileRef.current.value = "";

      // Auto-fill caption template
      const template = `👑 ${p.title}

${p.currency || "EUR"} ${p.price} · Cinematic Royal Streetwear

→ https://kirchero.store/products/${p.handle}

#KirchHero #WearYourCrown #StreetwearLuxury #CinematicStyle`;
      setCaption(template);
      toast.success(`${p.title} geladen 🛍️`);
    } catch (err) {
      toast.error("Netzwerkfehler beim Produkt-Import");
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte ein Bild auswählen");
      return;
    }
    setImageFile(f);
    setAiFilename(null);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setCaption("");
    setImageFile(null);
    setAiFilename(null);
    setPreview(null);
    setAiPrompt("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Schreib zuerst einen Prompt");
      return;
    }
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/ai/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          prompt: aiPrompt,
          reference_filename: templateFilename || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "KI-Bild fehlgeschlagen");
        return;
      }
      setAiFilename(data.filename);
      setImageFile(null);
      setPreview(`${API}${data.preview_path}`);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Bild generiert ✨");
    } catch (err) {
      toast.error("Netzwerkfehler bei der KI-Anfrage");
    } finally {
      setAiLoading(false);
    }
  };

  const uploadTemplate = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen (PNG empfohlen)");
      return;
    }
    setTemplateUploading(true);
    const fd = new FormData();
    fd.append("image", f);
    try {
      const res = await fetch(`${API}/api/admin/ai/upload-template`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Upload fehlgeschlagen");
        return;
      }
      setTemplateFilename(data.filename);
      setTemplatePreview(`${API}${data.preview_path}`);
      toast.success("Druckvorlage geladen 🎨");
    } catch (err) {
      toast.error("Netzwerkfehler beim Upload");
    } finally {
      setTemplateUploading(false);
      if (tplRef.current) tplRef.current.value = "";
    }
  };

  const clearTemplate = () => {
    setTemplateFilename(null);
    setTemplatePreview(null);
    if (tplRef.current) tplRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!imageFile && !aiFilename) {
      toast.error("Wähle erst ein Bild aus oder generiere eines");
      return;
    }
    setPosting(true);
    const fd = new FormData();
    if (aiFilename) {
      fd.append("existing_filename", aiFilename);
    } else {
      fd.append("image", imageFile);
    }
    fd.append("caption", caption);

    try {
      const res = await fetch(`${API}/api/admin/instagram/post`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Post fehlgeschlagen");
        return;
      }
      toast.success("Post veröffentlicht 🎉");
      reset();
      onPosted?.();
    } catch (err) {
      toast.error("Netzwerkfehler beim Posten");
    } finally {
      setPosting(false);
    }
  };

  const charCount = caption.length;
  const charLimit = 2200;

  return (
    <form
      onSubmit={submit}
      className="border border-white/10 bg-black/60 p-8"
      data-testid="instagram-composer"
    >
      <h2 className="font-display text-xl uppercase tracking-widest text-white mb-6 flex items-center justify-between">
        <span>Neuer Instagram-Post</span>
        <button
          type="button"
          onClick={() => setProductPickerOpen(true)}
          className="text-xs uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-1.5 transition-colors normal-case"
          data-testid="composer-pick-product"
        >
          🛍️ Shopify-Produkt einfügen
        </button>
      </h2>

      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onPick={onProductPick}
      />

      <div className="grid md:grid-cols-2 gap-8">
        {/* Image side */}
        <div>
          <label className="block text-xs uppercase tracking-widest text-white/60 mb-3">
            Bild (JPG, PNG, max. 8 MB)
          </label>

          <div
            onClick={() => fileRef.current?.click()}
            className="aspect-square border border-dashed border-white/20 hover:border-[#D4AF37] cursor-pointer flex items-center justify-center overflow-hidden transition-colors bg-black/40"
            data-testid="composer-image-dropzone"
          >
            {preview ? (
              <img
                src={preview}
                alt="Vorschau"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-white/40">
                <div className="text-4xl mb-2">+</div>
                <div className="text-xs uppercase tracking-widest">
                  Bild auswählen
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="hidden"
            data-testid="composer-image-input"
          />

          {(imageFile || aiFilename) && (
            <button
              type="button"
              onClick={() => {
                setImageFile(null);
                setAiFilename(null);
                setPreview(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
              className="mt-3 text-xs text-white/50 hover:text-white underline"
              data-testid="composer-remove-image"
            >
              Bild entfernen
            </button>
          )}

          {/* AI generation block */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <label className="block text-xs uppercase tracking-widest text-[#D4AF37] mb-2">
              ✨ KI-Bild generieren
            </label>
            <p className="text-[11px] text-white/40 mb-3 leading-relaxed">
              Beschreib die Szene — Burgundy/Gold-Branding wird automatisch hinzugefügt.
            </p>

            {/* Print template */}
            <div className="mb-4 p-3 border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div
                  onClick={() => tplRef.current?.click()}
                  className="w-16 h-16 border border-dashed border-white/20 hover:border-[#D4AF37] cursor-pointer flex items-center justify-center overflow-hidden bg-black/40 flex-shrink-0"
                  data-testid="composer-template-dropzone"
                >
                  {templatePreview ? (
                    <img
                      src={templatePreview}
                      alt="Druckvorlage"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-2xl text-white/40">+</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-white/70">
                    Druckvorlage (optional)
                  </div>
                  <div className="text-[10px] text-white/40 mt-0.5">
                    {templateFilename
                      ? "Wird auf Kleidung in der Szene gemappt"
                      : "PNG mit Transparenz funktioniert am besten"}
                  </div>
                  {templateFilename && (
                    <button
                      type="button"
                      onClick={clearTemplate}
                      className="text-[10px] text-white/50 hover:text-white underline mt-1"
                      data-testid="composer-template-clear"
                    >
                      Entfernen
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={tplRef}
                type="file"
                accept="image/*"
                onChange={uploadTemplate}
                className="hidden"
                data-testid="composer-template-input"
                disabled={templateUploading}
              />
              {templateUploading && (
                <div className="text-[10px] text-[#D4AF37] mt-2">Lade Vorlage hoch …</div>
              )}
            </div>

            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value.slice(0, 800))}
              placeholder={
                templateFilename
                  ? `z.B. "Junger Mann im Studio mit schwarzem T-Shirt — das Logo sitzt mittig auf der Brust, weiches Spotlight, Burgundy-Hintergrund"`
                  : `z.B. "Junger Mann mit dunkler Kapuze auf nasser Hauptstraße bei Nacht, Neonlichter in burgunderfarbenem Schimmer, cinematic 35mm grain"`
              }
              className="w-full min-h-[80px] bg-transparent border border-white/20 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-body text-xs resize-none"
              data-testid="composer-ai-prompt"
            />
            <button
              type="button"
              onClick={generateWithAI}
              disabled={aiLoading || !aiPrompt.trim()}
              className="mt-3 w-full bg-black border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-display uppercase tracking-widest text-xs py-2 disabled:opacity-40 transition-colors"
              data-testid="composer-ai-generate"
            >
              {aiLoading
                ? "Generiere … (ca. 15 Sek)"
                : templateFilename
                ? "Mockup mit Druckvorlage erzeugen"
                : "Mit KI generieren"}
            </button>
            {aiFilename && (
              <div className="mt-2 text-[10px] text-white/40">
                KI-Bild bereit — klick &quot;Posten&quot; um zu veröffentlichen
              </div>
            )}
          </div>
        </div>

        {/* Caption side */}
        <div className="flex flex-col">
          <label className="block text-xs uppercase tracking-widest text-white/60 mb-3">
            Caption & Hashtags
          </label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, charLimit))}
            placeholder={`Wear Your Crown. 👑\n\n#KirchHero #StreetwearLuxury #WearYourCrown`}
            className="flex-1 min-h-[260px] bg-transparent border border-white/20 focus:border-[#D4AF37] focus:outline-none px-4 py-3 text-white font-body text-sm resize-none"
            data-testid="composer-caption-input"
          />
          <div className="flex justify-between items-center text-xs text-white/40 mt-2">
            <span>Max. 30 Hashtags · IG limitiert auf 2.200 Zeichen</span>
            <span>
              {charCount} / {charLimit}
            </span>
          </div>

          <button
            type="submit"
            disabled={posting || (!imageFile && !aiFilename)}
            className="mt-6 bg-[#7B1E2A] hover:bg-[#651722] text-white font-display uppercase tracking-widest py-4 disabled:opacity-40 transition-colors"
            data-testid="composer-submit"
          >
            {posting ? "Veröffentliche …" : "Jetzt auf Instagram posten"}
          </button>
        </div>
      </div>
    </form>
  );
}

function History({ refreshKey }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/instagram/posts?limit=20`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        setPosts([]);
        return;
      }
      const data = await res.json();
      setPosts(data.posts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [refreshKey]);

  return (
    <div className="border border-white/10 bg-black/60 p-8" data-testid="history-panel">
      <h2 className="font-display text-xl uppercase tracking-widest text-white mb-6">
        Letzte Posts
      </h2>
      {loading ? (
        <div className="text-white/40 text-sm">Lade …</div>
      ) : posts.length === 0 ? (
        <div className="text-white/40 text-sm">Noch keine Posts veröffentlicht.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {posts.map((p) => (
            <a
              key={p.id}
              href={p.permalink || "#"}
              target="_blank"
              rel="noreferrer"
              className="group relative aspect-square overflow-hidden bg-black/40 block"
              data-testid="history-item"
            >
              <img
                src={p.image_url}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3 text-white">
                <div className="text-[10px] uppercase tracking-widest mb-1 text-[#D4AF37]">
                  {p.status === "failed" ? "Fehler" : "Veröffentlicht"}
                </div>
                <div className="text-xs line-clamp-3 font-body">
                  {p.caption || "—"}
                </div>
                <div className="text-[10px] mt-1 text-white/50">
                  {new Date(p.created_at).toLocaleString("de-DE")}
                </div>
              </div>
              {p.status === "failed" && (
                <div className="absolute top-2 right-2 bg-red-700 text-white text-[9px] uppercase tracking-widest px-2 py-0.5">
                  Fehler
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSocial() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [refreshKey, setRefreshKey] = useState(0);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  // Verify token on mount — if invalid, force re-login
  useEffect(() => {
    if (!authed) return;
    fetch(`${API}/api/admin/instagram/posts?limit=1`, {
      headers: { ...authHeaders() },
    }).then((r) => {
      if (r.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }
    });
  }, [authed]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Helmet>
        <title>Admin · KirchHero</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="font-display text-sm uppercase tracking-[0.3em] text-[#D4AF37]">
          KirchHero · Admin
        </div>
        {authed && (
          <div className="flex items-center gap-4">
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/api/admin/instagram/refresh-token`, {
                    method: "POST",
                    headers: { ...authHeaders() },
                  });
                  const data = await res.json();
                  if (!res.ok) {
                    toast.error(data.detail || "Token-Refresh fehlgeschlagen");
                    return;
                  }
                  toast.success(data.message || "Token verlängert");
                } catch (err) {
                  toast.error("Netzwerkfehler");
                }
              }}
              className="text-[11px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-1.5 transition-colors"
              data-testid="admin-refresh-token"
            >
              🔄 IG-Token verlängern
            </button>
            <button
              onClick={logout}
              className="text-xs uppercase tracking-widest text-white/60 hover:text-white"
              data-testid="admin-logout"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {!authed ? (
        <LoginCard onSuccess={() => setAuthed(true)} />
      ) : (
        <main className="max-w-6xl mx-auto px-6 py-12 space-y-10" data-testid="admin-main">
          <Composer onPosted={() => setRefreshKey((k) => k + 1)} />
          <History refreshKey={refreshKey} />
        </main>
      )}
    </div>
  );
}
